import moment from 'moment';
import {
  Course,
  CourseEnrollmentStatusEnum,
  CourseModalityEnum,
  LearnerStatusEnum,
} from 'commons/types';
import { CourseAvailabilityEnum } from 'commons/types/courseTypes';

interface CourseEnrollmentStatusData {
  courseAvailability?: CourseAvailabilityEnum;
  enrollmentStartDate?: string | Date;
  enrollmentEndDate?: string | Date;
  timezone?: string;
}

export const formatEnrollmentStatusText = (
  data: CourseEnrollmentStatusData,
  fallbackForOpen = '',
): string => {
  const { courseAvailability, enrollmentStartDate, enrollmentEndDate, timezone } = data || {};

  switch (courseAvailability) {
    case CourseAvailabilityEnum.UPCOMING:
      return `Enrollment opens on ${moment
        .tz(enrollmentStartDate, timezone)
        .format('MMM DD, YYYY')}`;
    case CourseAvailabilityEnum.CLOSED:
      return `Enrollment closed on ${moment
        .tz(enrollmentEndDate, timezone)
        .format('MMM DD, YYYY')}`;
    case CourseAvailabilityEnum.OPEN:
      return enrollmentEndDate
        ? `Enroll by ${moment.tz(enrollmentEndDate, timezone).format('MMM DD, YYYY')}`
        : fallbackForOpen;
    default:
      return fallbackForOpen;
  }
};

export const enrollmentStatusColours = {
  [CourseEnrollmentStatusEnum.NOT_ENROLLED]: '#F4F4F5',
  [CourseEnrollmentStatusEnum.PURCHASED]: '#F6D845',
  [CourseEnrollmentStatusEnum.DEPOSITED]: '#EAB543',
  [CourseEnrollmentStatusEnum.ENROLLED]: '#DD9BF5',
  [CourseEnrollmentStatusEnum.IN_PROGRESS]: '#56CCF2',
  [CourseEnrollmentStatusEnum.COMPLETED]: '#6FCF97',
};

export const calculateCourseEnrollmentStatus = (
  course: Course,
  isUserCourse: boolean,
): CourseEnrollmentStatusEnum => {
  if (course.membershipStatus === 'payment_pending' && course.userPaymentData) {
    return CourseEnrollmentStatusEnum.DEPOSITED;
  }

  if (!course.isLmsHosted && course.membershipStatus) {
    return CourseEnrollmentStatusEnum.PURCHASED;
  }

  if (!course.courseStatus && !isUserCourse) {
    return CourseEnrollmentStatusEnum.NOT_ENROLLED;
  }

  if (
    course.courseStatus &&
    course.courseStatus.toLowerCase() === LearnerStatusEnum.COMPLETED.toLowerCase()
  ) {
    return CourseEnrollmentStatusEnum.COMPLETED;
  }

  if (
    course.courseStatus &&
    course.courseStatus.toLowerCase() === LearnerStatusEnum.ACTIVE.toLowerCase()
  ) {
    return CourseEnrollmentStatusEnum.IN_PROGRESS;
  }

  return CourseEnrollmentStatusEnum.ENROLLED;
};

export const MODALITY_DISPLAY_NAME = {
  [CourseModalityEnum.ONLINE]: 'Online',
  [CourseModalityEnum.IN_PERSON]: 'In-Person',
  [CourseModalityEnum.IN_PERSON_UNDERSCORE]: 'In-Person',
  [CourseModalityEnum.HYBRID]: 'Hybrid',
};

export const calculateCourseStatus = (course: Course, isUserCourse = true): string => {
  if (course?.courseStatus?.toLowerCase() === LearnerStatusEnum.COMPLETED.toLowerCase()) {
    return CourseEnrollmentStatusEnum.COMPLETED;
  }

  if (course.membershipStatus === 'payment_pending' && course.userPaymentData) {
    return CourseEnrollmentStatusEnum.DEPOSITED;
  }

  if (course?.courseStatus?.toLowerCase() === LearnerStatusEnum.ACTIVE.toLowerCase()) {
    return CourseEnrollmentStatusEnum.IN_PROGRESS;
  }

  if (!isUserCourse) {
    return CourseEnrollmentStatusEnum.NOT_ENROLLED;
  }

  return CourseEnrollmentStatusEnum.ENROLLED;
};
